/* Plantica — the article page

   Two jobs: the contents list, and the rail that carries it. It is built from the headings in the article, so
   nobody types it and it cannot fall out of step with the text — and it stays
   empty and hidden until those headings are found, because a „Cuprins" with
   nothing under it is worse than none.

   The eleven articles in the shop are written with `h3` for their sections and
   no `h2` anywhere. Rather than insist on a level the content does not use, the
   script takes whichever it finds as the top one and the next as the nested
   one.

   The panel is written open and closed here on a phone, the same way round as
   the footer: with the script blocked a phone shows nothing at all rather than
   a row that will not open, which is the right trade for a list that is
   navigation and not content. `<details>` opens instantly by itself, so the
   height is animated here — the element stays a `<details>`, and a reader who
   asked for less motion still gets the instant one.

   THE RAIL

   Taller than the window, once it holds a contents list, two doctors and a
   product. A plain `position: sticky` would pin its top and hide its own foot
   for the whole article. So it drifts instead: `top` is walked down as the
   reader reads down, until the foot of the rail rests on the foot of the
   window, and back up the moment they scroll up. The rail itself never gets a
   scrollbar — that was the first answer here and a poor one, a second scroll
   region to get stuck in.

   Custom file. See CUSTOMIZATIONS.md. */

(() => {
  const SLUG_PREFIX = 'sectiunea-';
  const PHONE = '(max-width: 989px)';
  const WIDE = '(min-width: 990px)';
  // Fewer than two and it is not a contents list, it is a repeated heading.
  const WORTH_IT = 2;
  // Breathing room above the rail and below it.
  const GAP = 24;
  const OPEN_MS = 280;
  const EASE = 'cubic-bezier(0.22, 0.8, 0.28, 1)';

  const slug = (text) =>
    text
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);

  const headerHeight = () =>
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--plantica-header-height')) || 0;

  const smooth = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const build = (root) => {
    const body = root.querySelector('.plantica-article__body');
    const toc = root.querySelector('.plantica-article__toc');
    if (!body || !toc || toc.dataset.ready === 'true') return;

    const list = toc.querySelector('.plantica-article__toc-list');
    if (!list) return;

    // Whichever level the article actually uses is the top one.
    const top = body.querySelector('h2') ? 'h2' : 'h3';
    const deep = top === 'h2' ? 'h3' : 'h4';
    const heads = Array.from(body.querySelectorAll(`${top}, ${deep}`)).filter(
      (head) => head.textContent.trim() !== ''
    );

    if (heads.length < WORTH_IT) return;

    const taken = new Set();
    const links = heads.map((head) => {
      if (!head.id) {
        let id = SLUG_PREFIX + (slug(head.textContent) || 'x');
        let n = 2;
        while (taken.has(id) || document.getElementById(id)) {
          id = `${SLUG_PREFIX}${slug(head.textContent) || 'x'}-${n}`;
          n += 1;
        }
        head.id = id;
      }
      taken.add(head.id);

      const item = document.createElement('li');
      item.className = 'plantica-article__toc-item';
      if (head.tagName.toLowerCase() === deep) item.classList.add('plantica-article__toc-item--deep');

      const link = document.createElement('a');
      link.className = 'plantica-article__toc-link';
      link.href = `#${head.id}`;

      // The heading goes in a span of its own so the rule underneath it can be
      // as wide as the words rather than as wide as the row, which would put it
      // under the number too.
      const text = document.createElement('span');
      text.className = 'plantica-article__toc-text';
      text.textContent = head.textContent.trim();

      link.appendChild(text);
      item.appendChild(link);
      list.appendChild(item);
      return link;
    });

    toc.dataset.ready = 'true';
    toc.hidden = false;

    /* ---------- open on a wide screen, folded on a phone ---------- */

    const phone = window.matchMedia(PHONE);
    const summary = toc.querySelector('.plantica-article__toc-title');
    let playing = null;

    const fold = () => {
      if (playing) playing.cancel();
      toc.open = !phone.matches;
      list.style.removeProperty('height');
    };
    fold();
    phone.addEventListener('change', fold);

    /* The height of the panel, animated, while `<details>` keeps doing its own
       job. Opening sets `open` first and grows from nothing; closing shrinks
       and only then lets the element shut, or the content would vanish on the
       first frame. */
    const slide = (opening) => {
      if (playing) playing.cancel();

      const full = list.scrollHeight;
      playing = list.animate(
        [
          { height: `${opening ? 0 : full}px`, opacity: opening ? 0 : 1, marginBlockStart: opening ? '0px' : '16px' },
          { height: `${opening ? full : 0}px`, opacity: opening ? 1 : 0, marginBlockStart: opening ? '16px' : '0px' },
        ],
        { duration: OPEN_MS, easing: EASE }
      );

      playing.onfinish = () => {
        playing = null;
        if (!opening) toc.open = false;
      };
    };

    if (summary) {
      summary.addEventListener('click', (event) => {
        // Only where it is an accordion, and only for a reader who wants motion.
        if (!phone.matches || smooth() === false) return;
        event.preventDefault();
        if (toc.open) {
          slide(false);
        } else {
          toc.open = true;
          slide(true);
        }
      });
    }

    /* ---------- the section being read ---------- */

    const spy = () => {
      // A little below the header, so a heading counts as reached once it has
      // actually cleared it.
      const line = headerHeight() + 32;
      let current = links[0];
      heads.forEach((head, i) => {
        if (head.getBoundingClientRect().top <= line) current = links[i];
      });
      links.forEach((link) => link.classList.toggle('is-current', link === current));
    };

    /* ---------- the rail follows the reader ---------- */

    const rail = root.querySelector('.plantica-article__rail');
    const wide = window.matchMedia(WIDE);
    let railTop = null;
    let lastY = window.scrollY;

    const glide = () => {
      if (!rail) return;

      if (!wide.matches) {
        rail.style.removeProperty('top');
        railTop = null;
        lastY = window.scrollY;
        return;
      }

      const head = headerHeight() + GAP;
      const tall = rail.offsetHeight;
      const room = window.innerHeight;
      const y = window.scrollY;

      // Short enough to hold still: pin it under the header and leave it.
      if (tall + head + GAP <= room) {
        railTop = head;
        rail.style.top = `${head}px`;
        lastY = y;
        return;
      }

      // Taller than the window. `top` walks between the two ends by however far
      // the reader moved, so the rail never pins one edge and hides the other.
      if (railTop === null) railTop = head;
      const foot = room - tall - GAP;
      railTop = Math.min(head, Math.max(foot, railTop - (y - lastY)));
      lastY = y;
      rail.style.top = `${railTop}px`;
    };

    let pending = 0;
    const tick = () => {
      pending = 0;
      spy();
      glide();
    };

    const queue = () => {
      if (pending) return;
      pending = requestAnimationFrame(tick);
    };

    tick();
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    wide.addEventListener('change', queue);
    // Photographs land late and change how tall the rail is.
    window.addEventListener('load', queue);

    /* ---------- following a link ---------- */

    list.addEventListener('click', (event) => {
      const link = event.target.closest('.plantica-article__toc-link');
      if (!link) return;

      const head = document.getElementById(decodeURIComponent(link.hash.slice(1)));
      if (!head) return;

      event.preventDefault();
      // `scroll-margin-top` on the heading keeps it clear of the sticky header.
      head.scrollIntoView({ behavior: smooth() ? 'smooth' : 'auto', block: 'start' });
      history.replaceState(null, '', link.hash);

      // On a phone the list sits above the article; leaving it open would mean
      // scrolling past it again on the way back.
      if (phone.matches) toc.open = false;
    });
  };

  const start = () => {
    document.querySelectorAll('.plantica-article').forEach(build);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // The customizer replaces a section's markup wholesale.
  document.addEventListener('shopify:section:load', start);
})();
